import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { 
  Heart, 
  Mail, 
  MapPin, 
  Clock,
  ExternalLink,
  Menu,
  X,
  Award,
  Users,
  Stethoscope
} from 'lucide-react';

const HomePage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  // Logo URL
  const logoUrl = "https://www.yellowroseclinic.com/images/yrLogo1.png";

  const services = [
    {
      title: "Initial Assessment",
      duration: "60 minutes",
      price: "€285",
      description: "Comprehensive initial psychiatric assessment to understand your mental health needs and develop a personalized care plan.",
      bookingLink: "https://calendly.com/perinatalpsychiatry/initial-assessment"
    },
    {
      title: "Follow-Up Consultation",
      duration: "30-40 minutes",
      price: "€195",
      description: "Ongoing support and monitoring to ensure your treatment plan is effective and adjusted as needed.",
      bookingLink: "https://calendly.com/perinatalpsychiatry/follow-up"
    },
    {
      title: "Assessment with Report and Documentation",
      duration: "60 minutes",
      price: "€385",
      description: "Reports, letters, referrals, forms, and all other pertinent documentation will be issued after the standard consultation.",
      bookingLink: "https://calendly.com/perinatalpsychiatry/initial-assessment-report"
    }
  ];

  const resources = [
    {
      title: "Mental Health and Fertility Treatments",
      description: "Understanding the intersection of mental health and fertility care",
      link: 'files/Mental Health and Fertility Treatments.pdf'
    },
    {
      title: "Infertility Myths - Debunking Common Misconceptions",
      description: "Separating fact from fiction in fertility and mental health",
      link: 'files/Infertility Myths - Debunking Common Misconceptions.pdf'
    },
    {
      title: "Hormones & Mental Health - Understanding the Impact of Natural Cycles & Fertility Treatments",
      description: "Exploring the connection between hormonal changes and mental wellbeing",
      link: 'files/Hormones & Mental Health - Understanding the Impact of Natural Cycles & Fertility Treatments.pdf'
    },
    {
      title: "Depression, Anxiety, Trauma & Mental Health Struggles in Pregnancy & Postpartum After Infertility Treatments",
      description: "Support for mental health challenges during and after pregnancy",
      link: 'files/Depression, Anxiety, Trauma & Mental Health Struggles in Pregnancy & Postpartum After Infertility Treatments.pdf'
    },
    {
      title: "Recurrent Pregnancy Loss, Failed Fertility Cycles & Pregnancy After Loss (PAL)",
      description: "Navigating the emotional journey of pregnancy loss and subsequent pregnancies",
      link: 'files/Recurrent Pregnancy Loss, Failed Fertility Cycles & Pregnancy After Loss.pdf'
    },
    {
      title: "Recurrent Pregnancy Loss & Grief",
      description: "Understanding and processing grief after pregnancy loss",
      link: 'files/Recurrent Pregnancy Loss & Grief.pdf'
    },
    {
      title: "Psychotropic Medications & Fertility",
      description: "Important information about psychiatric medications during fertility treatment",
      link: 'files/Psychotropic Medications & Fertility.pdf'
    },
    {
      title: "Psychotherapy & Infertility",
      description: "Therapeutic approaches to support mental health during infertility",
      link: 'files/Psychotherapy & Infertility.pdf'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 via-white to-purple-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-purple-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <img 
                src={logoUrl}
                alt="Yellow Rose Logo"
                className="w-12 h-12 object-contain"
              />
              <div>
                <h1 className="text-xl font-semibold text-gray-900">Yellow Rose Mental Health Clinic</h1>
                <p className="text-sm text-gray-600">Caring for Minds, Nurturing Lives</p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex space-x-8">
              <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:text-purple-700 transition-colors font-medium">
                Home
              </button>
              <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-purple-700 transition-colors font-medium">
                About
              </button>
              <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-purple-700 transition-colors font-medium">
                Services
              </button>
              <button onClick={() => scrollToSection('resources')} className="text-gray-700 hover:text-purple-700 transition-colors font-medium">
                Resources
              </button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-purple-700 transition-colors font-medium">
                Contact
              </button>
            </nav>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden text-gray-700 hover:text-purple-700 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <nav className="md:hidden py-4 space-y-3 border-t border-purple-100">
              <button onClick={() => scrollToSection('home')} className="block w-full text-left text-gray-700 hover:text-purple-700 transition-colors font-medium py-2">
                Home
              </button>
              <button onClick={() => scrollToSection('about')} className="block w-full text-left text-gray-700 hover:text-purple-700 transition-colors font-medium py-2">
                About
              </button>
              <button onClick={() => scrollToSection('services')} className="block w-full text-left text-gray-700 hover:text-purple-700 transition-colors font-medium py-2">
                Services
              </button>
              <button onClick={() => scrollToSection('resources')} className="block w-full text-left text-gray-700 hover:text-purple-700 transition-colors font-medium py-2">
                Resources
              </button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left text-gray-700 hover:text-purple-700 transition-colors font-medium py-2">
                Contact
              </button>
            </nav>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            <span className="text-purple-700">Yellow Rose</span> <br />
            Mental Health Clinic
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-10">
            Compassionate, expert psychiatric care specializing in perinatal mental health and general adult psychiatry
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              onClick={() => scrollToSection('services')}
              className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-6 text-lg transition-all duration-300 hover:shadow-lg"
            >
              View Services
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => scrollToSection('contact')}
              className="border-2 border-purple-600 text-purple-700 hover:bg-purple-50 px-8 py-6 text-lg transition-all duration-300"
            >
              Get in Touch
            </Button>
          </div>
        </div>
      </section>

      {/* About Dr. Carolina Section */}
      <section id="about" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Dr. Carolina Giacobone</h2>
              <p className="text-lg font-medium text-purple-700 mb-4">
                MD, MRCPsych, Dip CBT, MSc Perinatal Mental Health, MA Perinatal Integrative Psychotherapy, Dip Perinatal Psychiatry
              </p>
              <p className="text-xl font-semibold text-gray-800 mb-6">
                General Adult Psychiatrist with a Special Interest in Perinatal Mental Health
              </p>

              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  As a passionate psychiatrist with 13 years of clinical psychiatric experience in both Argentina and the Republic of Ireland, I bring a diverse and global perspective to mental health care. I have worked across both public and private sectors, providing compassionate and expert support for individuals facing a range of mental health challenges, including anxiety, depression, trauma disorders, and in the last four years, maternal and perinatal mental health.
                </p>
                <p>
                  My approach integrates evidence-based therapies, including integrative psychotherapy and modern psychopharmacology, tailored to each patient's unique needs.
                </p>
                <p>
                  I hold an MD, MRCPsych, and multiple advanced qualifications, including a Diploma in Cognitive Behavioural Therapy (Dip CBT), an MSc in Perinatal Mental Health, an MA in Perinatal Integrative Psychotherapy, and a Diploma in Perinatal Psychiatry. I achieved honours degrees and top marks in all my academic achievements, highlighting my commitment to excellence.
                </p>
                <p>
                  In addition to my clinical work, I have extensive research and teaching experience, contributing to the advancement of the field. I am an active member of the Perinatal Psychiatry Special Interest Group (SIG) of the College of Psychiatrists of Ireland and the Perinatal and Infant Mental Health SIG of the Psychological Society of Ireland (PSI).
                </p>
                <p>
                  As a native speaker of both Spanish and English, I am able to connect with a broad range of people and families, ensuring a personalised and culturally sensitive approach.
                </p>
                <p>
                  At Yellow Rose Mental Health Clinic, I strive to provide a supportive, healing environment where individuals can receive personalised care. Whether through therapy, medication management, or guidance, my aim is to help everyone feel empowered to take control of their mental health and well-being. I believe in the power of connection, community, and collaboration to foster lasting change.
                </p>
              </div>

              <div className="grid grid-cols-3 gap-6 mt-8 pt-8 border-t border-gray-200">
                <div className="text-center">
                  <Award className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                  <p className="text-sm font-semibold text-gray-800">13 Years</p>
                  <p className="text-xs text-gray-600">Experience</p>
                </div>
                <div className="text-center">
                  <Users className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                  <p className="text-sm font-semibold text-gray-800">Bilingual</p>
                  <p className="text-xs text-gray-600">Spanish & English</p>
                </div>
                <div className="text-center">
                  <Stethoscope className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                  <p className="text-sm font-semibold text-gray-800">Specializing</p>
                  <p className="text-xs text-gray-600">Perinatal Care</p>
                </div>
              </div>
            </div>

            <div className="order-1 md:order-2">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-200 to-pink-200 rounded-3xl transform rotate-3"></div>
                <img 
                  src="https://www.yellowroseclinic.com/images/profile_caro.jpg"
                  alt="Dr. Carolina Giacobone"
                  className="relative rounded-3xl shadow-2xl w-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Services</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Professional psychiatric services tailored to your needs
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Card key={index} className="border-2 border-purple-100 hover:border-purple-300 transition-all duration-300 hover:shadow-xl bg-white">
                <CardHeader>
                  <CardTitle className="text-2xl text-gray-900">{service.title}</CardTitle>
                  <CardDescription className="flex items-center space-x-2 text-gray-600">
                    <Clock className="w-4 h-4" />
                    <span>{service.duration}</span>
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-gray-700">{service.description}</p>
                  <div className="pt-4 border-t border-gray-200">
                    <p className="text-4xl font-bold text-purple-700 mb-4">{service.price}</p>
                    <Button 
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white transition-all duration-300 hover:shadow-lg"
                      onClick={() => window.open(service.bookingLink, '_blank')}
                    >
                      Book Now
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Resources Section */}
      <section id="resources" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Resources</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Educational resources on mental health, fertility, and perinatal care
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {resources.map((resource, index) => (
              <Card 
                key={index}
                onClick={() => window.open(resource.link, '_blank')}
                className="border border-gray-200 hover:border-purple-300 transition-all duration-300 hover:shadow-lg cursor-pointer bg-gradient-to-br from-white to-purple-50 group"
              >
                <CardHeader>
                  <CardTitle className="text-lg text-gray-900 group-hover:text-purple-700 transition-colors flex items-start justify-between">
                    <span className="flex-1">{resource.title}</span>
                    <ExternalLink className="w-5 h-5 text-purple-600 flex-shrink-0 ml-2" />
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600">{resource.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Contact Us</h2>
            <p className="text-xl text-gray-600">
              Get in touch to schedule an appointment or learn more about our services
            </p>
          </div>

          <Card className="border-2 border-purple-100 shadow-xl bg-white">
            <CardContent className="p-8">
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-purple-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                    <p className="text-gray-600">yellowrosementalhealth@gmail.com</p>
                  </div>
                </div>

                <Separator />

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-purple-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Online consultations - Republic of Ireland</h3>
                    <p className="text-gray-600">Virtual appointments available</p>
                  </div>
                </div>

                <div className="pt-6">
                  <Button 
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white text-lg py-6 transition-all duration-300 hover:shadow-lg"
                    onClick={() => window.open('https://calendly.com/perinatalpsychiatry/initial-assessment', '_blank')}
                  >
                    Book an Appointment
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Terms & Conditions Section */}
      <section id="terms" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Terms & Conditions</h2>
          </div>

          <Card className="border border-gray-200 bg-white shadow-lg">
            <CardContent className="p-8 md:p-12 space-y-6">
              <div className="space-y-4">
                <h3 className="text-2xl font-bold text-gray-900">Yellow Rose Mental Health Clinic - Psychiatry Contract</h3>
                <p className="text-gray-700">
                  This contract is an agreement between Yellow Rose Mental Health Clinic, you (the patient), and your psychiatrist. Please read this document prior to booking. By booking a consultation, we acknowledge that you have read, understood, and agreed to the following terms and conditions.
                </p>
              </div>

              <Separator />

              <div className="space-y-3">
                <h4 className="text-xl font-semibold text-gray-900">1. Nature of Psychiatric Consultation</h4>
                <p className="text-gray-700">
                  Your psychiatrist is legally and ethically obliged to make clinical determinations based on current medical evidence and your provided medical history. The psychiatrist's recommendations may or may not align with your personal expectations and may include non-pharmacological interventions such as:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Psychotherapy</li>
                  <li>Lifestyle adjustments</li>
                  <li>Support groups</li>
                  <li>Referral to external services, including in-patient facilities</li>
                </ul>
                <p className="text-gray-700">
                  All psychiatric reports and recommendations provided are final and based on professional clinical judgment.
                </p>
              </div>

              <Separator />

              <div className="space-y-3">
                <h4 className="text-xl font-semibold text-gray-900">2. Attendance & Cancellation Policy</h4>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Patients must attend appointments punctually and without interruption.</li>
                  <li>If the appointment has not commenced within 10 minutes, it will be cancelled without refund.</li>
                  <li>Cancellations must be made at least 7 days in advance; otherwise, fees will be charged in full.</li>
                  <li>Online consultations require the patient to have their camera turned on for the psychiatrist to conduct an adequate assessment.</li>
                </ul>
              </div>

              <Separator />

              <div className="space-y-3">
                <h4 className="text-xl font-semibold text-gray-900">3. Medication Policy</h4>
                <p className="text-gray-700">If medication is prescribed, you acknowledge that:</p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>You will be informed about usage and potential risks.</li>
                  <li>If you experience any severe side effects (e.g., chest pain, palpitations, severe dizziness, or severe mood changes), you must immediately stop the medication and seek urgent medical care (GP or A&E).</li>
                  <li>ADHD medication: You may be required to provide recent blood tests, BMI, weight, height, and cardiovascular checks before starting treatment.</li>
                  <li>Prescriptions will only be sent to your nominated pharmacy and GP—they will not be provided directly to the patient.</li>
                  <li>Your GP and pharmacist retain the right to delay or refuse to dispense medication if clinically indicated.</li>
                </ul>
              </div>

              <Separator />

              <div className="space-y-3">
                <h4 className="text-xl font-semibold text-gray-900">4. Fees & Payment Policy</h4>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Consultation fees must be paid in advance to secure an appointment.</li>
                  <li>The number of sessions, appointment duration, and treatment plan will be determined by the psychiatrist based on your clinical needs.</li>
                  <li>No refunds are provided once a consultation has commenced.</li>
                </ul>
              </div>

              <Separator />

              <div className="space-y-3">
                <h4 className="text-xl font-semibold text-gray-900">5. Record Keeping & Data Protection</h4>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Clinical notes are stored securely and retained for 8 years after last contact (or until age 25 for minors if treatment concluded before age 18).</li>
                  <li>Your personal data will be processed in compliance with the General Data Protection Regulation (GDPR) (EU) 2016/679 and Data Protection Act 2018 (Ireland).</li>
                  <li>Patient details may be shared within the Yellow Rose Mental Health Clinic multidisciplinary team (MDT) for optimal care. Additionally, your psychiatrist may liaise with your GP and other healthcare professionals to ensure comprehensive, coordinated care.</li>
                </ul>
              </div>

              <Separator />

              <div className="space-y-3">
                <h4 className="text-xl font-semibold text-gray-900">6. Limits of Confidentiality</h4>
                <p className="text-gray-700">
                  While confidentiality is a cornerstone of psychiatric care, there are exceptions under which Yellow Rose Mental Health Clinic is legally obliged to disclose information, including:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Child Protection: Under the Children First Act 2015, we must report concerns about child abuse or neglect to Tusla (Child and Family Agency).</li>
                  <li>Harm to Self or Others: If there is a serious risk of harm, we may notify your GP, emergency contacts, or emergency services.</li>
                  <li>Legal Obligations: We must comply with legal requests, including subpoenas or investigations by An Garda Síochána.</li>
                  <li>Criminal Activity: Under Irish law, we must report involvement in serious criminal offences.</li>
                </ul>
              </div>

              <Separator />

              <div className="space-y-3">
                <h4 className="text-xl font-semibold text-gray-900">7. Consent for Minors</h4>
                <p className="text-gray-700">For patients under 18 years of age, the following applies:</p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>A parent or legal guardian must provide written informed consent for psychiatric assessment and treatment.</li>
                  <li>If the minor is 16 or 17 years old, they may provide consent for treatment under the Non-Fatal Offences Against the Person Act 1997.</li>
                  <li>Where a minor is deemed to have sufficient understanding and maturity (Gillick competence), they may make decisions about their own care. However, safeguarding concerns may override this.</li>
                  <li>In cases involving custody disputes, both parents/legal guardians must provide written consent unless a court order specifies otherwise.</li>
                </ul>
              </div>

              <Separator />

              <div className="space-y-3">
                <h4 className="text-xl font-semibold text-gray-900">8. Safeguarding & Emergency Contact</h4>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>You consent to Yellow Rose Mental Health Clinic contacting your GP and/or next of kin if there are concerns about immediate risk to yourself or others.</li>
                  <li>If an emergency psychiatric crisis arises, you must seek immediate care via GP, A&E, or emergency mental health services.</li>
                </ul>
              </div>

              <Separator />

              <div className="space-y-3">
                <h4 className="text-xl font-semibold text-gray-900">9. Professional Conduct</h4>
                <p className="text-gray-700">The psychiatrist reserves the right to terminate the session immediately if the patient is:</p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Incoherent</li>
                  <li>Under the influence of alcohol or drugs</li>
                  <li>Abusive or threatening</li>
                  <li>Non-compliant with clinical recommendations</li>
                </ul>
              </div>

              <Separator />

              <div className="space-y-3">
                <h4 className="text-xl font-semibold text-gray-900">10. Agreement to Terms</h4>
                <p className="text-gray-700">By booking an assessment, you confirm that you:</p>
                <ol className="list-decimal list-inside space-y-2 text-gray-700 ml-4">
                  <li>Understand and accept the terms outlined above.</li>
                  <li>Agree to follow medical recommendations provided by your psychiatrist.</li>
                  <li>Acknowledge the confidentiality limits and legal reporting obligations.</li>
                  <li>Understand the cancellation policy and fee structure.</li>
                </ol>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <img 
                  src={logoUrl}
                  alt="Yellow Rose Logo"
                  className="w-10 h-10 object-contain"
                />
                <div>
                  <h3 className="text-lg font-semibold">Yellow Rose</h3>
                  <p className="text-sm text-gray-400">Mental Health Clinic</p>
                </div>
              </div>
              <p className="text-gray-400 text-sm">
                Caring for Minds, Nurturing Lives
              </p>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <button onClick={() => scrollToSection('about')} className="text-gray-400 hover:text-purple-400 transition-colors">
                    About Dr. Carolina
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('services')} className="text-gray-400 hover:text-purple-400 transition-colors">
                    Services
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('resources')} className="text-gray-400 hover:text-purple-400 transition-colors">
                    Resources
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('contact')} className="text-gray-400 hover:text-purple-400 transition-colors">
                    Contact
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Contact</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>Online consultations - Republic of Ireland</li>
                <li>yellowrosementalhealth@gmail.com</li>
              </ul>
            </div>
          </div>

          <Separator className="bg-gray-700 mb-8" />

          <div className="text-center text-sm text-gray-400">
            <p>&copy; {new Date().getFullYear()} Yellow Rose Mental Health Clinic. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
